import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import { ShopContext } from "../context/shopContext.js";
import { useMarket } from "../context/MarketContext.jsx";
import PairWithCarousel from "./PairWithSection.jsx";
import SEO from "./SEO.jsx";

// Stable constant outside the component
const PRODUCTS_WITHOUT_SIZES = [
  "8561505632573",
  "8561183719741",
  "8561020272957",
  "8551840153917",
];

// Products where options[0] is Color and options[1] is Size
const COLOR_FIRST_PRODUCTS = ["9535256691005"];

// helper: extract numeric product ID from Shopify GID
const extractNumericId = (gid) => {
  if (!gid) return "";
  const match = String(gid).match(/Product\/(\d+)/);
  return match ? match[1] : "";
};

// helper: get option value by name from a variant
const getOptionValue = (variant, name) =>
  variant?.selectedOptions?.find(
    (o) => o?.name?.trim().toLowerCase() === name.trim().toLowerCase(),
  )?.value;

// normalise labels (trims stray spaces)
const formatOptionLabel = (value) =>
  String(value ?? "").trim().replace(/\s+/g, " ");

function ProductDetails({ productHandle, collectionHandle, legacyId }) {
  const params = useParams();
  // Handle-based identifier (SEO URLs)
  const handle = productHandle || params.productHandle || null;
  // Legacy numeric ID from route (for /product/:id)
  const routeNumericId = legacyId || params.id || "";

  const { fetchProductWithId, product, addItemsToCart, openCart } =
    useContext(ShopContext);

  // Read market so we can re-fetch product when country changes
  const { market } = useMarket();

  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedPlacement, setSelectedPlacement] = useState("");

  const galleryRef = useRef(null);
  const mobileGalleryRef = useRef(null);
  const [mobileIndex, setMobileIndex] = useState(0);

  // --- FETCH LOGIC (handle first, fallback to ID) ---
  useEffect(() => {
    // Prefer fetching by handle for SEO URLs
    if (handle) {
      fetchProductWithId(null, market?.country, { mode: "handle", handle });
      return;
    }

    // Legacy numeric ID path (/product/:id)
    if (routeNumericId) {
      const gid = `gid://shopify/Product/${routeNumericId}`;
      fetchProductWithId(gid, market?.country);
    }
  }, [handle, routeNumericId, fetchProductWithId, market?.country]);

  // Compute numeric product ID based on loaded product,
  // falling back to route numeric ID if needed.
  const numericId = extractNumericId(product?.id) || routeNumericId || "";

  // Guard against showing the previous product while a new one loads
  const isWrongProduct =
    (handle && product?.handle && product.handle !== handle) ||
    (routeNumericId &&
      extractNumericId(product?.id) &&
      extractNumericId(product.id) !== String(routeNumericId));

  // Build requestedGid used for guards & preloading (ID-route only)
  const requestedGid = routeNumericId
    ? `gid://shopify/Product/${routeNumericId}`
    : null;

  const getImgSrc = (img) => img?.url || img?.src || "";

  const imgPriority = (i) =>
    i === 0
      ? { loading: "eager", fetchPriority: "high" }
      : { loading: "lazy", fetchPriority: "low" };

  const buildSrcSet = (u) => {
    if (!u) return undefined;
    try {
      const widths = [320, 480, 640, 800, 1200];
      return widths
        .map((w) => {
          const url = new URL(u);
          url.searchParams.set("width", String(w));
          url.searchParams.set("format", "webp");
          return `${url.toString()} ${w}w`;
        })
        .join(", ");
    } catch {
      return undefined;
    }
  };

  // Desktop drag gallery
  useEffect(() => {
    if (!galleryRef.current) return;

    const gallery = galleryRef.current;

    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;

    const handleMouseDown = (e) => {
      isDragging = true;
      gallery.classList.add("dragging");
      startX = e.pageX - gallery.offsetLeft;
      scrollLeft = gallery.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const x = e.pageX - gallery.offsetLeft;
      const walk = (x - startX) * 1.5;
      gallery.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUpOrLeave = () => {
      isDragging = false;
      gallery.classList.remove("dragging");
    };

    gallery.addEventListener("mousedown", handleMouseDown);
    gallery.addEventListener("mousemove", handleMouseMove);
    gallery.addEventListener("mouseup", handleMouseUpOrLeave);
    gallery.addEventListener("mouseleave", handleMouseUpOrLeave);

    return () => {
      gallery.removeEventListener("mousedown", handleMouseDown);
      gallery.removeEventListener("mousemove", handleMouseMove);
      gallery.removeEventListener("mouseup", handleMouseUpOrLeave);
      gallery.removeEventListener("mouseleave", handleMouseUpOrLeave);
    };
  }, []);

  // Track mobile carousel index
  useEffect(() => {
    const el = mobileGalleryRef.current;
    if (!el) return;
    const onScroll = () => {
      const i = Math.round(el.scrollLeft / el.clientWidth);
      setMobileIndex(i);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Reset scroll when product changes
  useEffect(() => {
    if (!product?.id) return;
    setMobileIndex(0);
    if (mobileGalleryRef.current) {
      mobileGalleryRef.current.scrollLeft = 0;
    }
    if (galleryRef.current) {
      galleryRef.current.scrollLeft = 0;
    }
  }, [product?.id]);

  const handleQuantityChange = (value) => {
    if (value >= 1) setQuantity(value);
  };

  // ---------------------------
  // Preload remaining images
  // ---------------------------
  useEffect(() => {
    if (!product?.images?.length) return;
    if (requestedGid && product.id !== requestedGid) return;
    product.images.slice(1).forEach((img) => {
      const u = getImgSrc(img);
      if (!u) return;
      const pre = new Image();
      pre.decoding = "async";
      pre.src = u;
    });
  }, [product?.id, requestedGid]);

  // ---------------------------
  // EARLY GUARD (skeleton)
  // ---------------------------
  if (!product?.id || isWrongProduct) {
    return (
      <div className="relative md:w-full md:h-auto mt-10">
        {/* Desktop skeleton */}
        <div className="hidden md:flex w-full h-[80vh]">
          <div className="w-full h-full bg-gray-100 animate-pulse" />
        </div>

        {/* Mobile skeleton */}
        <div
          className="md:hidden w-full bg-gray-100 animate-pulse"
          style={{ height: "70vh" }}
        />

        {/* Details skeleton */}
        <div className="relative md:w-full py-8 md:px-12 bg-gray-100 bg-opacity-50 backdrop-blur-lg">
          <div className="w-2/3 mx-auto">
            <div className="h-6 w-1/2 mx-auto bg-gray-200 animate-pulse rounded mb-4" />
            <div className="h-4 w-32 mx-auto bg-gray-200 animate-pulse rounded" />
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------
  // VARIANTS, SOLD-OUT STATE, PRICE
  // ---------------------------
  const variants = product?.variants || [];

  // Is this a color-first product (e.g. Bandera Belt)?
  const isColorFirstProduct = COLOR_FIRST_PRODUCTS.includes(numericId);

  // Product is sold out if no variant is available
  const productSoldOut =
    variants.length > 0
      ? variants.every((v) => v.availableForSale === false)
      : product.availableForSale === false;

  const hasSizeOption = product?.options?.some(
    (o) => o?.name?.trim().toLowerCase() === "size",
  );

  const isProductWithoutSize =
    PRODUCTS_WITHOUT_SIZES.includes(numericId) || !hasSizeOption;

  // For personalised items / multi-option products,
  // size is available if ANY matching variant is available.
  const isSizeAvailable = (sizeValue) => {
    if (!variants.length) return false;
    return variants.some((v) => {
      const size = getOptionValue(v, "Size");
      const material = getOptionValue(v, "Material");
      const color = getOptionValue(v, "Color");
      if (size !== sizeValue) return false;
      if (selectedMaterial && material !== selectedMaterial) return false;
      if (selectedColor && color !== selectedColor) return false;
      return v.availableForSale !== false;
    });
  };

  // Check if a color value has any available variant
  const isColorAvailable = (colorValue) => {
    if (!variants.length) return false;
    return variants.some((v) => {
      const color = getOptionValue(v, "Color");
      if (color !== colorValue) return false;
      if (selectedSize) {
        const size = getOptionValue(v, "Size");
        if (size !== selectedSize) return false;
      }
      return v.availableForSale !== false;
    });
  };

  // Find the Size option object (by name, not by index)
  const sizeOption = product?.options?.find(
    (o) => o?.name?.trim().toLowerCase() === "size",
  );

  // Find the Color option object (for color-first products)
  const colorOption = isColorFirstProduct
    ? product?.options?.find(
        (o) => o?.name?.trim().toLowerCase() === "color",
      )
    : null;

  // PRICE selection (respect sold-out state only for styling)
  let variantForPrice = null;
  if (variants.length) {
    if (isProductWithoutSize) {
      variantForPrice = variants[0];
    } else if (isColorFirstProduct) {
      // Try to find a variant matching both color and size selections
      variantForPrice =
        variants.find((v) => {
          const color = getOptionValue(v, "Color");
          const size = getOptionValue(v, "Size");
          const colorMatch = !selectedColor || color === selectedColor;
          const sizeMatch = !selectedSize || size === selectedSize;
          return colorMatch && sizeMatch;
        }) || variants[0];
    } else {
      variantForPrice =
        variants.find((v) => getOptionValue(v, "Size") === selectedSize) ||
        variants[0];
    }
  }

  const priceAmount =
    variantForPrice?.price?.amount != null
      ? Number(variantForPrice.price.amount)
      : null;

  const currency =
    variantForPrice?.price?.currencyCode ||
    product?.variants?.[0]?.price?.currencyCode ||
    "GBP";

  const compareAmount =
    variantForPrice?.compareAtPrice?.amount != null
      ? Number(variantForPrice.compareAtPrice.amount)
      : null;

  const isOnSale =
    compareAmount != null && priceAmount != null && compareAmount > priceAmount;

  const firstVariant = product?.variants?.[0];
  const fallbackPriceText = firstVariant
    ? `${Math.floor(firstVariant.price.amount)} ${
        firstVariant.price.currencyCode
      }`
    : "";

  const currentPriceText =
    priceAmount != null ? `${Math.floor(priceAmount)} ${currency}` : fallbackPriceText;

  const comparePriceText =
    compareAmount != null ? `${Math.floor(compareAmount)} ${currency}` : null;

  const handleAddToCart = () => {
    setError("");

    if (productSoldOut) {
      setError("This product is sold out.");
      return;
    }

    if (!isProductWithoutSize && !selectedSize) {
      setError("Please select a size.");
      return;
    }

    if (isColorFirstProduct && !selectedColor) {
      setError("Please select a colour.");
      return;
    }

    // personalised product – require material + message
    const isPersonalised = numericId === "8562465145149";

    if (isPersonalised && !selectedMaterial) {
      setError("Please select a material.");
      return;
    }

    if (quantity < 1) {
      setError("Please select a valid quantity.");
      return;
    }

    if (isPersonalised && !personalMessage.trim()) {
      setError("Please enter a personal message.");
      return;
    }

    if (isPersonalised && !selectedPlacement) {
      setError("Please select an embroidery placement.");
      return;
    }    

    // Find the variant to add to the cart
    let selectedVariant;

    if (isProductWithoutSize) {
      selectedVariant = product.variants?.[0];
    } else {
      selectedVariant = variants.find((v) => {
        const size = getOptionValue(v, "Size");
        const material = getOptionValue(v, "Material");
        const color = getOptionValue(v, "Color");
        if (size !== selectedSize) return false;
        if (selectedMaterial && material !== selectedMaterial) return false;
        if (selectedColor && color !== selectedColor) return false;
        return true;
      });

      if (!selectedVariant) {
        setError("Selected option is unavailable.");
        return;
      }

      if (selectedVariant.availableForSale === false) {
        setError("This option is sold out.");
        return;
      }
    }

    // Build line item properties only if present
    const attributes = [];
    if (selectedMaterial) {
      attributes.push({ key: "Material", value: selectedMaterial });
    }
    if (personalMessage.trim()) {
      attributes.push({ key: "Message", value: personalMessage.trim() });
    }
    if (selectedPlacement) {
      attributes.push({ key: "Placement", value: selectedPlacement });
    }
    
    const priceAmount = selectedVariant?.price?.amount;
    const priceCurrency = selectedVariant?.price?.currencyCode;

    const value = priceAmount
      ? Number(priceAmount) * Number(quantity)
      : 0;

    addItemsToCart(selectedVariant.id, quantity, attributes, {
      content_ids: [selectedVariant.id],
      value,
      currency: priceCurrency || "GBP",
    });

    openCart();

  };

  // SEO
  const canonicalPath = handle
    ? collectionHandle
      ? `/collections/${collectionHandle}/products/${handle}`
      : `/products/${handle}`
    : routeNumericId
    ? `/product/${routeNumericId}`
    : "/";

  const primaryImage =
    product.images && product.images.length > 0
      ? product.images[0]?.url || product.images[0]?.src
      : undefined;

  let metaDescription = product.description || "";
  if (!metaDescription && product.descriptionHtml) {
    metaDescription = product.descriptionHtml.replace(/<[^>]+>/g, " ");
  }
  metaDescription = metaDescription.replace(/\s+/g, " ").trim();
  if (!metaDescription) {
    metaDescription = "Underwear and clothing from This Belongs To.";
  }
  if (metaDescription.length > 160) {
    metaDescription = `${metaDescription.slice(0, 157)}...`;
  }

  return (
    <>
      <SEO
        title={product.title}
        description={metaDescription}
        path={canonicalPath}
        image={primaryImage}
      />
      <div className="relative md:w-full md:h-auto mt-10">
        {/* Horizontal Scrollable Gallery for Desktop Only */}
        <div
          ref={galleryRef}
          className="hidden md:flex w-full h-[80vh] overflow-hidden md:overflow-x-auto snap-x snap-mandatory"
        >
          {product.images.map((image, index) => (
            <div
              key={index}
              className="flex-shrink-0 snap-center"
              style={{ flex: "0 0 auto", height: "100%" }}
            >
              <img
                src={getImgSrc(image)}
                srcSet={buildSrcSet(getImgSrc(image))}
                sizes="(max-width: 768px) 100vw, 70vw"
                alt={`Product ${index + 1}`}
                className="w-auto h-full object-contain"
                decoding="async"
                {...imgPriority(index)}
              />
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div
          ref={mobileGalleryRef}
          className="flex md:hidden w-full overflow-x-auto snap-x snap-mandatory scroll-smooth"
          style={{ WebkitOverflowScrolling: "touch", height: "70vh" }}
        >
          {product.images.map((image, index) => {
            const src = getImgSrc(image);
            return (
              <div
                key={index}
                className="flex-shrink-0 snap-center w-full flex items-center justify-center bg-white"
                style={{ flex: "0 0 100%" }}
              >
                <img
                  src={src}
                  srcSet={buildSrcSet(src)}
                  sizes="100vw"
                  alt={`Product ${index + 1}`}
                  className="h-full w-auto object-contain mx-auto"
                  decoding="async"
                  {...imgPriority(index)}
                />
              </div>
            );
          })}
        </div>

        {/* Product Details Section */}
        <div className="relative md:w-full py-8 md:px-12 bg-gray-100 bg-opacity-50 backdrop-blur-lg flex flex-col space-y-6">
          <div className="w-2/3 mx-auto text-center">
            <h1 className="text-2xl italic mb-2 font-[300]">{product.title}</h1>

            {/* PRICE - greyed out if product is sold out */}
            <div
              className={`text-lg mb-4 italic ${
                productSoldOut ? "opacity-60" : ""
              }`}
            >
              {isOnSale ? (
                <div className="space-x-2">
                  <span className="line-through opacity-60">
                    {comparePriceText}
                  </span>
                  <span>{currentPriceText}</span>
                </div>
              ) : (
                <span>{currentPriceText}</span>
              )}
            </div>

            {/* Description */}
            {(() => {
              let html = product.descriptionHtml || "";
              if (!html && product.description) {
                html = `<p>${product.description
                  .trim()
                  .replace(/\n{2,}/g, "</p><p>")
                  .replace(/\n/g, "<br />")}</p>`;
              }
              html = html
                .replace(/(?:<br\s*\/?>\s*){2,}/gi, "</p><p>")
                .replace(/<p>\s*<\/p>/g, "");
              html = html.replace(
                /<p>(\s*(?:<[^>]+>\s*)*)\*/gi,
                '<p class="footnote">$1*',
              );

              return (
                <div
                  className="
                    mb-6 text-center
                    [&>p]:mb-4 [&>p]:leading-relaxed
                    [&_.footnote]:text-sm [&_.footnote]:opacity-80
                  "
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              );
            })()}

            {/* Colour picker - only for color-first products */}
            {isColorFirstProduct && colorOption && (
              <div className="space-y-2 mb-4">
                <h3 className="text-md">
                  Colour:{" "}
                  <span className="text-customBlue">
                    {selectedColor || ""}
                  </span>
                </h3>
                <div className="flex justify-center flex-wrap gap-3">
                  {colorOption.values.map((color) => {
                    const colorValue = color?.value ?? color;
                    const colorSoldOut =
                      productSoldOut || !isColorAvailable(colorValue);

                    return (
                      <button
                        key={colorValue}
                        type="button"
                        onClick={() =>
                          !colorSoldOut && setSelectedColor(colorValue)
                        }
                        disabled={colorSoldOut}
                        className={`px-4 py-2 border mb-2 ${
                          colorSoldOut
                            ? "border-gray-300 text-gray-400 opacity-60 cursor-not-allowed"
                            : selectedColor === colorValue
                            ? "bg-customNavy text-white"
                            : "border-customNavy hover:bg-customNavy hover:text-white"
                        }`}
                      >
                        {formatOptionLabel(colorValue)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sizes - only show if product actually has a Size option */}
            {/* For color-first products, use the sizeOption found by name instead of options[0] */}
            {!isProductWithoutSize && (
              <div className="space-y-2">
                <h3 className="text-md">
                  {numericId === "14898299601277" ? "Amount:" : "Size:"}
                  <span className="text-customBlue">
                    {selectedSize || ""}
                  </span>
                </h3>
                <div className="flex justify-center flex-wrap gap-3">
                  {(isColorFirstProduct && sizeOption
                    ? sizeOption.values
                    : product.options[0]?.values
                  )?.map((size) => {
                    const sizeValue = size?.value ?? size;
                    const sizeSoldOut =
                      productSoldOut || !isSizeAvailable(sizeValue);

                    return (
                      <button
                        key={sizeValue}
                        type="button"
                        onClick={() =>
                          !sizeSoldOut && setSelectedSize(sizeValue)
                        }
                        disabled={sizeSoldOut}
                        className={`px-4 py-2 border mb-2 ${
                          sizeSoldOut
                            ? "border-gray-300 text-gray-400 opacity-60 cursor-not-allowed"
                            : selectedSize === sizeValue
                            ? "bg-customNavy text-white"
                            : "border-customNavy hover:bg-customNavy hover:text-white"
                        }`}
                      >
                        {formatOptionLabel(sizeValue)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!isProductWithoutSize && numericId !== "14898299601277" && (
              <div className="mb-4 italic hover:underline">
                <a href="/pages/size-guide">Size guide</a>
              </div>
            )}

            {/* Materials */}
            {numericId === "8562465145149" && (
              <div className="space-y-2 mb-4">
                <h3 className="text-md">Materials</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {product.options[1]?.values.map((material) => {
                    const value = material?.value ?? material;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setSelectedMaterial(value)}
                        className={`px-4 py-2 border ${
                          selectedMaterial === value
                            ? "bg-customNavy text-white"
                            : "border-customNavy hover:bg-customNavy hover:text-white"
                        }`}
                      >
                        {formatOptionLabel(value)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Embroidery placement */}
            {numericId === "8562465145149" && (
              <div className="space-y-2 mb-4">
                <h3 className="text-md">Embroidery placement</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {["Front", "Back"].map((placement) => (
                    <button
                      key={placement}
                      type="button"
                      onClick={() => setSelectedPlacement(placement)}
                      className={`px-4 py-2 border ${
                        selectedPlacement === placement
                          ? "bg-customNavy text-white"
                          : "border-customNavy hover:bg-customNavy hover:text-white"
                      }`}
                    >
                      {placement}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Personal Message */}
            {numericId === "8562465145149" && (
              <div className="mb-4">
                <h3 className="text-md">Personal Message</h3>
                <textarea
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  className="w-full h-20 border p-2 mt-2"
                  placeholder="Enter your personal message here"
                />
              </div>
            )}

            {/* Quantity Selector */}
            <div className="flex justify-center items-center space-x-4 mb-4">
              <h3 className="text-md">Quantity</h3>
              <div className="flex border border-customNavy">
                <button
                  type="button"
                  onClick={() => handleQuantityChange(quantity - 1)}
                  className="px-4 py-2"
                >
                  –
                </button>
                <div className="px-4 py-2 border-l border-r border-customNavy">
                  {quantity}
                </div>
                <button
                  type="button"
                  onClick={() => handleQuantityChange(quantity + 1)}
                  className="px-4 py-2"
                >
                  +
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && <p className="text-red-500">{error}</p>}

            {/* Add to Cart / Sold out Button */}
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={productSoldOut}
              className={`w-3/4 md:w-1/4 px-6 py-3 border transition ${
                productSoldOut
                  ? "border-gray-300 text-gray-400 cursor-not-allowed opacity-60"
                  : "border-customNavy hover:bg-customNavy hover:text-white"
              }`}
            >
              {productSoldOut ? "Sold out" : "Add to bag"}
            </button>

            {/* Pair with / related products */}
            <PairWithCarousel relatedProducts={product.relatedProducts} />
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductDetails;